/**
 * Insights filter state (date range + time lens) AND the fetch/rollup cache
 * (Fix Spec C). Registered app-wide in App.tsx's ModuleProviders so filter
 * selections AND fetched data survive cross-module navigation and tab
 * switches inside Insights itself.
 *
 * CRITICAL: this provider is mounted at the app root (inside AuthProvider,
 * before any route match) and MUST NOT fetch anything at mount. Every fetch
 * is lazy — triggered only by `ensureSiteData`/`ensureRangeData`, which only
 * `useInsightsData.ts` calls, which only an actually-mounted Insights page
 * calls. A user who never opens /insights generates zero requests here.
 *
 * Caching model:
 * - `siteCache` (siteId -> studies/coordinators/delegations/portalAccess)
 *   and `rangeCache` (`${siteId}|${from}|${to}` -> effort/visits/tz) are
 *   ref-held Maps, not React state — mutating a Map doesn't need to force a
 *   render on its own, so a small `dataTick` counter state is bumped after
 *   every settle to give consumers a reason to re-read the caches.
 * - Each entry carries a monotonic `version`. `rollupCache` (keyed the same
 *   as rangeCache) memoizes the pure rollup build and only recomputes when
 *   the site/range entry it was built from has a different version than the
 *   one currently cached — so a `refresh()` that replaces siteCache data
 *   correctly invalidates rollups already computed against the old data.
 * - `error` entries are NOT treated as "have it" — `ensureSiteData` /
 *   `ensureRangeData` only skip a key whose status is `loading` or `ready`;
 *   an `error` key retries on the next `ensure` call.
 * - Dedupe is that `loading` marker itself — the entry sitting in the Map IS
 *   the in-flight record, so two near-simultaneous `ensure` calls for the same
 *   key (StrictMode's dev double-mount, or navigating between the two Insights
 *   pages before the first fetch resolves) collapse onto one request. No
 *   promise is stored: nothing ever awaited one, and a cached promise nobody
 *   reads is a claim the code doesn't keep.
 * - Because that marker is load-bearing, LRU eviction skips `loading` entries
 *   (evicting the oldest SETTLED one instead, and tolerating a temporary
 *   over-cap when every entry is in flight) — dropping an in-flight marker
 *   would both un-dedupe the key and let its settle re-insert an entry the
 *   cap already accounted for.
 * - Each settle writes only if its own loading entry is still the one in the
 *   Map (identity check). That makes a superseded fetch — evicted, `refresh`ed
 *   out from under, or flushed by an auth change — discard its result instead
 *   of resurrecting a key the cache deliberately dropped.
 * - Otherwise there is no fetchSeq/race guard: each response writes into ITS
 *   OWN key (`siteId`, or `${siteId}|${from}|${to}`), so a late response for a
 *   since-abandoned site/range simply updates that key's entry — it can
 *   never land on top of the currently-displayed one.
 * - `/portal-access` is the one site-fetch slice allowed to fail alone: it
 *   only decorates authorization chips with portal-login counts, so it
 *   catches into `[]` (with a console.warn) rather than taking studies,
 *   coordinators and delegations down with it. The other three stay
 *   collectively fatal and visible — an error box plus a retry on the next
 *   `ensure` — because a page missing any of them is not a page.
 * - Both caches (and the rollup memo) are flushed when the authenticated user
 *   id changes, including to null. Sign-out here does not reload the tab
 *   (ToolHub calls `supabase.auth.signOut()` and the provider stays mounted),
 *   so without this the next user in the same tab could read the previous
 *   user's cached rows for a site they also have access to — stale, not a
 *   privilege escalation, but still someone else's answer.
 */

import React, { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import {
  RangeKey,
  TimeLens,
  rangeDates,
  siteLocalToday,
  EffortRow,
  VisitStatsRow,
  StudyInfo,
  Coordinator,
  DelegationProtocol,
  PortalAccessRow,
} from '../types';
import { StudyRollup, CoordinatorRollup, buildStudyRollups, buildCoordinatorRollups } from '../lib/rollups';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

type FetchStatus = 'loading' | 'ready' | 'error';

interface SiteData {
  studies: StudyInfo[];
  coordinators: Coordinator[];
  delegations: DelegationProtocol[];
  portalAccess: PortalAccessRow[];
}

interface RangeData {
  effort: EffortRow[];
  visits: VisitStatsRow[];
  tz: string | null;
}

interface SiteCacheEntry {
  status: FetchStatus;
  data: SiteData | null;
  error: string | null;
  fetchedAt: number | null;
  version: number;
}

interface RangeCacheEntry {
  status: FetchStatus;
  data: RangeData | null;
  error: string | null;
  fetchedAt: number | null;
  version: number;
}

interface RollupCacheEntry {
  siteVersion: number;
  rangeVersion: number;
  studyRollups: Map<string, StudyRollup>;
  coordinatorRollups: Map<string, CoordinatorRollup>;
}

// Approximate LRU: Map iteration order is insertion order, and every touch
// (hit or write) deletes+re-inserts the key, so the front of the Map is
// always the least-recently-touched entry — cheap and good enough for an
// ~8-entry soft cap (RangeCache: a handful of distinct 7d/30d/90d/custom
// windows a session realistically visits).
const RANGE_CACHE_LIMIT = 8;

/**
 * Evicts oldest-first but NEVER an in-flight (`loading`) entry: that entry is
 * the dedupe marker `ensureRangeData` skips on, so evicting it would let a
 * concurrent ensure fire a duplicate request for a fetch already running.
 * When every entry is in flight there is nothing safe to drop, so the cache
 * is allowed to sit temporarily over cap — it comes back under as those
 * entries settle and the next ensure evicts them.
 */
function evictOldestRangeEntry(rangeCache: Map<string, RangeCacheEntry>, rollupCache: Map<string, RollupCacheEntry>) {
  while (rangeCache.size > RANGE_CACHE_LIMIT) {
    let evictable: string | undefined;
    for (const [key, entry] of rangeCache) {
      if (entry.status !== 'loading') {
        evictable = key;
        break;
      }
    }
    if (evictable === undefined) break; // all in flight — stay over cap for now
    rangeCache.delete(evictable);
    rollupCache.delete(evictable); // orphaned without its range entry — drop it too
  }
}

const rangeCacheKey = (siteId: string, from: string, to: string) => `${siteId}|${from}|${to}`;

interface InsightsContextValue {
  // ---- Filter state ----
  range: RangeKey;
  from: string;
  to: string;
  lens: TimeLens;
  setRange: (key: RangeKey) => void;
  setCustomRange: (from: string, to: string) => void;
  setLens: (lens: TimeLens) => void;

  // ---- Data plane (consumed by useInsightsData.ts) ----
  getSiteEntry: (siteId: string) => SiteCacheEntry | undefined;
  getRangeEntry: (siteId: string, from: string, to: string) => RangeCacheEntry | undefined;
  getRollups: (
    siteId: string,
    from: string,
    to: string
  ) => { studyRollups: Map<string, StudyRollup>; coordinatorRollups: Map<string, CoordinatorRollup> };
  ensureSiteData: (siteId: string) => void;
  ensureRangeData: (siteId: string, from: string, to: string) => void;
  /** Evicts the cached site+range data and re-fetches all 5 endpoints. */
  refresh: (siteId: string, from: string, to: string) => void;
}

const InsightsContext = createContext<InsightsContextValue | undefined>(undefined);

export function InsightsProvider({ children }: { children: ReactNode }) {
  const { currentSite, user } = useAuth();
  const tz = currentSite?.timezone || null;
  const authUserId = user?.id ?? null;

  // ---- Filter state: range + lens ----------------------------------------

  const [range, setRangeState] = useState<RangeKey>('30d');
  const [customFrom, setCustomFrom] = useState<string | null>(null);
  const [customTo, setCustomTo] = useState<string | null>(null);
  const [lens, setLens] = useState<TimeLens>('with_crc');

  // Site-local "today". Not read directly below — it exists purely to force
  // a re-render (and therefore a re-derivation of `presetRange`) when the
  // site-local calendar day rolls over, even if nothing else changes. A
  // browser tab left open on Insights across midnight must roll a preset
  // range's from/to forward on its own; without this, the dates would stay
  // frozen at whatever they were when the provider last happened to render.
  const [today, setToday] = useState(() => siteLocalToday(tz));
  useEffect(() => {
    setToday(siteLocalToday(tz)); // site switch (tz changed) — recompute now
  }, [tz]);
  useEffect(() => {
    const id = setInterval(() => {
      setToday((prev) => {
        const next = siteLocalToday(tz);
        return next === prev ? prev : next;
      });
    }, 60_000); // cheap: one Intl format + string compare, not a fetch
    return () => clearInterval(id);
  }, [tz]);

  // Presets are derived at READ time (render), not frozen at mount — the
  // fix for the Session-3 "computed once, browser-local" finding. Plain
  // computation, not useMemo: rangeDates is cheap (Date arithmetic + one
  // Intl format), and deriving it fresh every render is exactly what makes
  // the `today` tick above (state that nothing here reads directly) take
  // effect — it exists purely to force this component to re-render at a
  // site-local midnight rollover; once it does, this line naturally picks
  // up the new day because it calls `new Date()` itself. Custom ranges are
  // untouched by this: they're whatever the user typed.
  const presetRange = range === 'custom' ? null : rangeDates(range, tz);
  const from = range === 'custom' ? customFrom ?? today : presetRange!.from;
  const to = range === 'custom' ? customTo ?? today : presetRange!.to;

  const setRange = useCallback(
    (key: RangeKey) => {
      if (key === 'custom') {
        // Freeze whatever is currently displayed (preset-derived, or a
        // prior custom window) as the starting custom range.
        setCustomFrom(from);
        setCustomTo(to);
      }
      setRangeState(key);
    },
    [from, to]
  );

  const setCustomRange = useCallback((f: string, t: string) => {
    setRangeState('custom');
    if (f) setCustomFrom(f);
    if (t) setCustomTo(t);
  }, []);

  // ---- Data plane: ref-held caches ----------------------------------------

  const siteCacheRef = useRef<Map<string, SiteCacheEntry>>(new Map());
  const rangeCacheRef = useRef<Map<string, RangeCacheEntry>>(new Map());
  const rollupCacheRef = useRef<Map<string, RollupCacheEntry>>(new Map());
  const versionCounterRef = useRef(0);

  // Bumped after every cache settle so context consumers (Insights pages,
  // via useInsightsData) re-render and re-read the (now-updated) caches.
  // Mutating the ref-held Maps above does not itself trigger a re-render.
  const [, setDataTick] = useState(0);
  const bump = useCallback(() => setDataTick((t) => t + 1), []);

  // Caches are per-site, not per-user, and this provider outlives a sign-out:
  // ToolHub's sign-out calls supabase.auth.signOut() without reloading the
  // tab, so ModuleProviders (and these refs) stay mounted. Flush everything
  // when the authenticated identity changes — including to null — so the next
  // user in the same browser tab can never read rows fetched for the previous
  // one. In-flight fetches started under the old identity discard their
  // results via the settle-time identity check below.
  const authUserIdRef = useRef<string | null>(authUserId);
  useEffect(() => {
    if (authUserIdRef.current === authUserId) return;
    authUserIdRef.current = authUserId;
    siteCacheRef.current.clear();
    rangeCacheRef.current.clear();
    rollupCacheRef.current.clear();
    bump();
  }, [authUserId, bump]);

  const ensureSiteData = useCallback(
    (siteId: string) => {
      if (!siteId) return;
      const existing = siteCacheRef.current.get(siteId);
      if (existing && (existing.status === 'loading' || existing.status === 'ready')) return;

      const version = ++versionCounterRef.current;
      const loadingEntry: SiteCacheEntry = {
        status: 'loading',
        data: existing?.data ?? null,
        error: null,
        fetchedAt: existing?.fetchedAt ?? null,
        version,
      };
      // This fetch owns the key only while ITS loading entry is the one in the
      // Map. A `refresh()` or an auth flush that lands mid-flight replaces or
      // removes it, and this response must then be dropped rather than
      // resurrect data the cache deliberately discarded.
      const stillOurs = () => siteCacheRef.current.get(siteId) === loadingEntry;
      Promise.all([
        api.get(`/api/ops-insights/studies?site_id=${siteId}`),
        api.get(`/api/ops-insights/coordinators?site_id=${siteId}`),
        api.get(`/api/ops-insights/delegations?site_id=${siteId}`),
        // Portal access is purely decorative — it adds "N portal logins" to
        // the authorization chips. It is the one slice allowed to fail alone:
        // a portal-access outage must not blank studies, coordinators and
        // delegations too. The other three stay collectively fatal.
        api.get(`/api/ops-insights/portal-access?site_id=${siteId}`).catch((err) => {
          console.warn('Insights: portal-access unavailable — authorization chips omit portal logins', err);
          return { data: [] };
        }),
      ])
        .then(([studiesRes, coordsRes, delegRes, accessRes]) => {
          if (!stillOurs()) return;
          siteCacheRef.current.set(siteId, {
            status: 'ready',
            data: {
              studies: Array.isArray(studiesRes.data) ? studiesRes.data : [],
              coordinators: Array.isArray(coordsRes.data) ? coordsRes.data : [],
              delegations: Array.isArray(delegRes.data) ? delegRes.data : [],
              portalAccess: Array.isArray(accessRes.data) ? accessRes.data : [],
            },
            error: null,
            fetchedAt: Date.now(),
            // A FRESH version, not the loading entry's — the settle always
            // changes `data` (empty/stale -> fetched), so it must invalidate
            // any rollup memoized against the loading-phase entry, even
            // though that entry's own version never gets read again once
            // replaced here.
            version: ++versionCounterRef.current,
          });
        })
        .catch((err) => {
          console.error('Error loading insights data:', err);
          if (!stillOurs()) return;
          siteCacheRef.current.set(siteId, {
            status: 'error',
            data: existing?.data ?? null,
            error: err.response?.data?.error || 'Failed to load insights data',
            fetchedAt: existing?.fetchedAt ?? null,
            version: ++versionCounterRef.current,
          });
        })
        .finally(() => {
          bump();
        });
      siteCacheRef.current.set(siteId, loadingEntry);
      bump();
    },
    [bump]
  );

  const ensureRangeData = useCallback(
    (siteId: string, rangeFrom: string, rangeTo: string) => {
      if (!siteId || !rangeFrom || !rangeTo) return;
      const key = rangeCacheKey(siteId, rangeFrom, rangeTo);
      const existing = rangeCacheRef.current.get(key);
      if (existing && (existing.status === 'loading' || existing.status === 'ready')) {
        // Touch for LRU recency without changing the entry itself.
        rangeCacheRef.current.delete(key);
        rangeCacheRef.current.set(key, existing);
        return;
      }

      const version = ++versionCounterRef.current;
      const loadingEntry: RangeCacheEntry = { status: 'loading', data: null, error: null, fetchedAt: null, version };
      // See ensureSiteData: this response writes only while its own loading
      // entry still holds the key. Eviction can no longer drop a `loading`
      // entry, so in practice this catches `refresh()` and the auth flush.
      const stillOurs = () => rangeCacheRef.current.get(key) === loadingEntry;
      api
        .get(`/api/ops-insights/effort?site_id=${siteId}&from=${rangeFrom}&to=${rangeTo}`)
        .then((res) => {
          if (!stillOurs()) return;
          rangeCacheRef.current.delete(key);
          rangeCacheRef.current.set(key, {
            status: 'ready',
            data: {
              effort: Array.isArray(res.data?.effort) ? res.data.effort : [],
              visits: Array.isArray(res.data?.visits) ? res.data.visits : [],
              tz: res.data?.tz || null,
            },
            error: null,
            fetchedAt: Date.now(),
            // Fresh version — see the matching comment in ensureSiteData.
            version: ++versionCounterRef.current,
          });
        })
        .catch((err) => {
          console.error('Error loading effort data:', err);
          // A failed range must not keep a previous range's numbers on
          // screen — ranges are keyed independently, so there IS no
          // previous data under THIS key; data stays null.
          if (!stillOurs()) return;
          rangeCacheRef.current.delete(key);
          rangeCacheRef.current.set(key, {
            status: 'error',
            data: null,
            error: err.response?.data?.error || 'Failed to load effort data',
            fetchedAt: null,
            version: ++versionCounterRef.current,
          });
        })
        .finally(() => {
          bump();
        });
      rangeCacheRef.current.set(key, loadingEntry);
      evictOldestRangeEntry(rangeCacheRef.current, rollupCacheRef.current);
      bump();
    },
    [bump]
  );

  const getSiteEntry = useCallback((siteId: string) => siteCacheRef.current.get(siteId), []);
  const getRangeEntry = useCallback(
    (siteId: string, rangeFrom: string, rangeTo: string) => rangeCacheRef.current.get(rangeCacheKey(siteId, rangeFrom, rangeTo)),
    []
  );

  const getRollups = useCallback((siteId: string, rangeFrom: string, rangeTo: string) => {
    const key = rangeCacheKey(siteId, rangeFrom, rangeTo);
    const siteEntry = siteCacheRef.current.get(siteId);
    const rangeEntry = rangeCacheRef.current.get(key);
    const siteVersion = siteEntry?.version ?? 0;
    const rangeVersion = rangeEntry?.version ?? 0;

    const cached = rollupCacheRef.current.get(key);
    if (cached && cached.siteVersion === siteVersion && cached.rangeVersion === rangeVersion) {
      return { studyRollups: cached.studyRollups, coordinatorRollups: cached.coordinatorRollups };
    }

    const studies = siteEntry?.data?.studies ?? [];
    const effort = rangeEntry?.data?.effort ?? [];
    const visits = rangeEntry?.data?.visits ?? [];
    const studyRollups = buildStudyRollups(studies, effort, visits);
    const coordinatorRollups = buildCoordinatorRollups(effort, visits);
    rollupCacheRef.current.delete(key);
    rollupCacheRef.current.set(key, { siteVersion, rangeVersion, studyRollups, coordinatorRollups });
    return { studyRollups, coordinatorRollups };
  }, []);

  const refresh = useCallback(
    (siteId: string, rangeFrom: string, rangeTo: string) => {
      if (!siteId) return;
      siteCacheRef.current.delete(siteId);
      const key = rangeCacheKey(siteId, rangeFrom, rangeTo);
      rangeCacheRef.current.delete(key);
      rollupCacheRef.current.delete(key);
      bump();
      ensureSiteData(siteId);
      ensureRangeData(siteId, rangeFrom, rangeTo);
    },
    [bump, ensureSiteData, ensureRangeData]
  );

  return (
    <InsightsContext.Provider
      value={{
        range,
        from,
        to,
        lens,
        setRange,
        setCustomRange,
        setLens,
        getSiteEntry,
        getRangeEntry,
        getRollups,
        ensureSiteData,
        ensureRangeData,
        refresh,
      }}
    >
      {children}
    </InsightsContext.Provider>
  );
}

export function useInsights(): InsightsContextValue {
  const ctx = useContext(InsightsContext);
  if (!ctx) throw new Error('useInsights must be used within InsightsProvider');
  return ctx;
}

export default InsightsProvider;
